import { PlusOutlined, SendOutlined } from '@ant-design/icons';
import { App, Button, Popconfirm, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { deleteReminder, listReminders, listTemplates, resetReminderDefaults, updateReminder } from '../api';
import { ReminderModal, SendNowModal } from '../components/reminder-modal';
import ScanStaffSection from '../components/scan-staff-section';
import {
  GalaReminder,
  REMINDER_STATUS_META,
  errorText,
  formatVn,
  guestGroupOptions,
} from '../contract';
import { reminderLabel } from '../lib/reminder-label';
import styles from '../styles.module.scss';

interface Props {
  eventId: number;
}

const EDITABLE = ['SCHEDULED', 'CANCELLED'];

/** Tab Nhắc lịch: các mốc push nhắc khách + "Gửi ngay" + "Ai được quét" (SPEC mục 6, 8). */
const RemindersTab = ({ eventId }: Props) => {
  const { message } = App.useApp();
  const [rows, setRows] = useState<GalaReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<GalaReminder | null>(null);
  const [nowOpen, setNowOpen] = useState(false);
  const [templateGroups, setTemplateGroups] = useState<string[]>([]);

  const reload = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setLoading(true);
      try {
        setRows(await listReminders(eventId));
      } catch (e) {
        message.error(errorText(e, 'Không tải được các mốc nhắc.'));
      } finally {
        setLoading(false);
      }
    },
    [eventId, message]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  // Gợi ý tên nhóm khách từ các mẫu vé; lỗi thì thôi (ô nhóm vẫn gõ tay được).
  useEffect(() => {
    let alive = true;
    listTemplates(eventId)
      .then((ts) => {
        if (alive) setTemplateGroups(ts.map((t) => t.guestGroup).filter((g): g is string => !!g));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [eventId]);

  const groupOptions = useMemo(
    () => guestGroupOptions(templateGroups, rows.flatMap((r) => r.groups ?? [])),
    [templateGroups, rows]
  );

  const run = async (task: () => Promise<unknown>, ok: string, fail: string) => {
    setBusy(true);
    try {
      await task();
      message.success(ok);
      await reload(false);
    } catch (e) {
      message.error(errorText(e, fail));
    } finally {
      setBusy(false);
    }
  };

  const columns: ColumnsType<GalaReminder> = [
    { title: 'Mốc', width: 170, render: (_: unknown, r) => <span className={styles.nowrap}>{reminderLabel(r)}</span> },
    { title: 'Giờ gửi', dataIndex: 'sendAt', width: 150, render: (v: string) => <span className={styles.nowrap}>{formatVn(v)}</span> },
    {
      title: 'Tiêu đề',
      dataIndex: 'title',
      render: (v: string, r) => (
        <div>
          <div>{v}</div>
          {r.audience === 'GROUPS' && <span className={styles.subtle}>Nhóm: {(r.groups ?? []).join(', ')}</span>}
        </div>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 120,
      render: (s: GalaReminder['status']) => {
        const meta = REMINDER_STATUS_META[s];
        return <Tag color={meta?.color}>{meta?.text ?? s}</Tag>;
      },
    },
    {
      title: 'Đã gửi',
      width: 110,
      render: (_: unknown, r) =>
        r.status === 'SENT' || r.status === 'SENDING' ? (
          <span>
            {r.sentCount}
            {r.failCount > 0 && <span className={styles.unlinked}> · lỗi {r.failCount}</span>}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: '',
      width: 230,
      render: (_: unknown, r) => (
        <Space size={0} wrap>
          <Button
            size="small"
            type="link"
            disabled={!EDITABLE.includes(r.status)}
            onClick={() => {
              setEditing(r);
              setEditorOpen(true);
            }}
          >
            Sửa
          </Button>
          {r.status === 'SCHEDULED' && (
            <Button
              size="small"
              type="link"
              onClick={() => run(() => updateReminder(eventId, r.id, { status: 'CANCELLED' }), 'Đã tắt mốc nhắc.', 'Không tắt được mốc nhắc.')}
            >
              Tắt
            </Button>
          )}
          {r.status === 'CANCELLED' && (
            <Button
              size="small"
              type="link"
              onClick={() => run(() => updateReminder(eventId, r.id, { status: 'SCHEDULED' }), 'Đã bật lại mốc nhắc.', 'Không bật lại được mốc nhắc.')}
            >
              Bật lại
            </Button>
          )}
          <Popconfirm
            title="Xoá mốc nhắc này?"
            okText="Xoá"
            cancelText="Không"
            okButtonProps={{ danger: true }}
            disabled={r.status === 'SENT' || r.status === 'SENDING'}
            onConfirm={() => run(() => deleteReminder(eventId, r.id), 'Đã xoá mốc nhắc.', 'Không xoá được mốc nhắc.')}
          >
            <Button size="small" type="link" danger disabled={r.status === 'SENT' || r.status === 'SENDING'}>
              Xoá
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <section className={styles.card} aria-label="Mốc nhắc lịch">
        <h3 className={styles.cardTitle}>Mốc nhắc lịch</h3>
        <div className={styles.toolbar}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditing(null);
              setEditorOpen(true);
            }}
          >
            Thêm mốc
          </Button>
          <Button icon={<SendOutlined />} onClick={() => setNowOpen(true)}>
            Gửi ngay
          </Button>
          <Popconfirm
            title="Đặt lại mốc mặc định?"
            description="Các mốc theo ngày diễn ra còn đang chờ sẽ bị huỷ và tạo lại bốn mốc mặc định. Mốc đã gửi giữ nguyên."
            okText="Đặt lại"
            cancelText="Không"
            onConfirm={() => run(() => resetReminderDefaults(eventId), 'Đã đặt lại mốc mặc định.', 'Không đặt lại được mốc mặc định.')}
          >
            <Button loading={busy}>Đặt lại mốc mặc định</Button>
          </Popconfirm>
        </div>
        <Table<GalaReminder>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText:
              'Chưa có mốc nhắc nào. Đặt "Ngày giờ diễn ra" ở tab Thông tin rồi bấm Lưu là có ngay 4 mốc mặc định (kể cả khi còn bản nháp; chỉ gửi sau khi Công bố). Sự kiện đã có ngày mà vẫn trống thì bấm "Đặt lại mốc mặc định".',
          }}
        />
      </section>

      <ScanStaffSection eventId={eventId} />

      <ReminderModal
        open={editorOpen}
        eventId={eventId}
        editing={editing}
        groupOptions={groupOptions}
        onClose={() => setEditorOpen(false)}
        onSaved={() => reload(false)}
      />
      <SendNowModal
        open={nowOpen}
        eventId={eventId}
        groupOptions={groupOptions}
        onClose={() => setNowOpen(false)}
        onSent={() => reload(false)}
      />
    </div>
  );
};

export default RemindersTab;
